import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import { TagDto } from '../models/tag'

export default class TagStore {
  tags: TagDto[] = []
  pendingTags: TagDto[] = []
  loading = false

  constructor () {
    makeAutoObservable(this)
  }

  get tagsByCategory () {
    return this.tags.reduce(
      (acc, tag) => {
        const cat = tag.category
        if (!acc[cat]) acc[cat] = []
        acc[cat].push(tag)
        return acc
      },
      {} as Record<number, TagDto[]>
    )
  }

  loadTags = async (category?: number, q?: string) => {
    this.loading = true
    try {
      const list = await agent.Tags.list({ category, q })
      runInAction(() => {
        this.tags = list ?? []
        this.loading = false
      })
    } catch {
      runInAction(() => (this.loading = false))
      throw new Error('Failed to load tags')
    }
  }

  loadPendingTags = async () => {
    this.loading = true
    try {
      const list = await agent.Tags.listPending()
      runInAction(() => {
        this.pendingTags = list ?? []
        this.loading = false
      })
    } catch {
      runInAction(() => (this.loading = false))
      throw new Error('Failed to load pending tags')
    }
  }

  createTag = async (values: { name: string; displayName: string; category: number }) => {
    const created = await agent.Tags.create(values)
    runInAction(() => {
      this.tags = [...this.tags, created]
    })
    return created
  }

  approveTag = async (id: string) => {
    await agent.Tags.approve(id)
    runInAction(() => {
      this.pendingTags = this.pendingTags.filter((t) => t.id !== id)
      const t = this.tags.find((x) => x.id === id)
      if (t) t.status = 1
      else this.loadTags()
    })
  }

  rejectTag = async (id: string) => {
    await agent.Tags.reject(id)
    runInAction(() => {
      this.pendingTags = this.pendingTags.filter((t) => t.id !== id)
    })
  }

  applyTag = async (tagId: string, targetType: number, targetId: string) => {
    return await agent.Tags.applyTag({ tagId, targetType, targetId })
  }

  removeTag = async (tagId: string, targetType: number, targetId: string) => {
    await agent.Tags.removeTag({ tagId, targetType, targetId })
  }
}
